import { Fragment } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LucideIcon, ArrowRight, CheckCircle2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import EditorialSteps, { Step } from "@/components/EditorialSteps";
import TechnologyStack, { TechnologyCategory } from "@/components/TechnologyStack";
import ServicesGrid from "@/components/ServicesGrid";
import SystemsTimeline, { TimelinePhase } from "@/components/SystemsTimeline";
import SystemShift, { SystemShiftItem } from "@/components/SystemShift";
import DataFlowchart, { FlowNode, FlowConnection } from "@/components/DataFlowchart";
import CapabilityMatrix, { MatrixCategory } from "@/components/CapabilityMatrix";
import EngagementShape, { EngagementPhase } from "@/components/EngagementShape";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import { ServiceBlock, DEFAULT_BLOCK_ORDER } from "@/lib/serviceBlocks";
import {
  heroVariant,
  sectionVariant,
  staggerContainer,
  itemVariant,
  slideInLeft,
  viewportOnce,
  viewportOnceNear,
  glowVariant,
  problemSectionVariant,
  problemStaggerContainer,
  problemAccentLineVariant,
  problemBulletVariant,
  problemHeaderVariant,
} from "@/lib/motion";

// ─── Types ───────────────────────────────────────────────────────

export interface ServiceComponent {
  name: string;
  icon: LucideIcon;
  components: string[];
  outcome: string;
}

export interface Technology {
  category: string;
  items: string[];
}

export interface TechnologyCategoryConfig {
  title: string;
  icon: LucideIcon;
  items: string[];
}

export interface FAQ {
  question: string;
  answer: string;
}

export interface Qualification {
  forYou: string[];
  notForYou: string[];
}

export interface SubServicePageProps {
  seoTitle: string;
  seoDescription: string;
  seoKeywords?: string[];
  canonicalUrl?: string;
  category: string;
  categoryLabel: string;
  headline: string;
  subheadline: string;
  heroSecondaryText?: string;
  ctaText?: string;
  ctaLink?: string;
  problemTitle: string;
  problems: string[];
  solutionTitle?: string;
  solutionPoints?: string[];
  systemTitle: string;
  systemDescription?: string;
  services: ServiceComponent[];
  processTitle: string;
  processSteps: Step[];
  timelinePhases?: TimelinePhase[];
  systemShift?: {
    title?: string;
    beforeTitle?: string;
    afterTitle?: string;
    items: SystemShiftItem[];
  };
  dataFlowchart?: {
    title?: string;
    label?: string;
    description?: string;
    nodes: FlowNode[];
    connections: FlowConnection[];
  };
  capabilityMatrix?: {
    title?: string;
    label?: string;
    description?: string;
    categories: MatrixCategory[];
  };
  engagementShape?: {
    title?: string;
    description?: string;
    phases?: EngagementPhase[];
  };
  platformEcosystems?: {
    title?: string;
    intro: string[];
    platformMention: string;
    problems: string[];
    reassurance: string;
  };
  technologies?: Technology[];
  technologyCategories?: TechnologyCategoryConfig[];
  techTitle?: string;
  techDescription?: string;
  qualification: Qualification;
  faqs: FAQ[];
  finalCtaTitle: string;
  finalCtaDescription: string;
  finalCtaButtonText?: string;
  /** Optional: CMS-driven block ordering. If present, sections render in this order. */
  blocks?: ServiceBlock[];
}

// ─── Section Renderers ───────────────────────────────────────────

function HeroSection({
  categoryLabel, headline, subheadline, heroSecondaryText, ctaText = "Start a serious conversation", ctaLink = "/contact",
}: Pick<SubServicePageProps, "categoryLabel" | "headline" | "subheadline" | "heroSecondaryText" | "ctaText" | "ctaLink">) {
  return (
    <section className="relative pt-20 pb-6 md:pt-28 md:pb-10 lg:pt-36 lg:pb-14 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <motion.div className="absolute top-0 right-1/4 w-[800px] h-[800px] rounded-full" variants={glowVariant} initial="hidden" animate="visible" style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.06) 0%, transparent 70%)" }} />
      </div>
      <div className="container-wide relative z-10">
        <div className="max-w-3xl">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
            <motion.div variants={heroVariant} className="flex items-center gap-3 mb-4">
              <div className="accent-bar" />
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">{categoryLabel}</span>
            </motion.div>
            <motion.h1 variants={heroVariant} className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground mb-6 leading-[1.1]">{headline}</motion.h1>
            <motion.p variants={heroVariant} className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-4 max-w-2xl">{subheadline}</motion.p>
            {heroSecondaryText && <motion.p variants={heroVariant} className="text-base text-muted-foreground/80 leading-relaxed mb-8 max-w-2xl">{heroSecondaryText}</motion.p>}
            <motion.div variants={heroVariant}>
              <Button size="lg" className="group" asChild><Link to={ctaLink}>{ctaText}<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link></Button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ProblemSection({ problemTitle, problems }: Pick<SubServicePageProps, "problemTitle" | "problems">) {
  return (
    <section className="py-10 md:py-12 lg:py-16 border-t border-border">
      <div className="container-wide">
        <motion.div className="max-w-3xl" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-30%" }} variants={problemSectionVariant}>
          <motion.div variants={problemHeaderVariant}>
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">The Problem</span>
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-10">{problemTitle}</h2>
          </motion.div>
          <motion.div className="relative" variants={problemStaggerContainer}>
            <motion.div className="absolute left-0 top-0 bottom-0 w-0.5 bg-primary/15" variants={problemAccentLineVariant} style={{ transformOrigin: "top" }} />
            <div className="space-y-5 pl-6">
              {problems.map((problem, index) => (
                <motion.div key={index} variants={problemBulletVariant} className="group relative">
                  <div className="absolute -left-6 top-0 bottom-0 w-0.5 bg-transparent transition-colors duration-200 md:group-hover:bg-primary" />
                  <p className="text-foreground/80 transition-colors duration-200 md:group-hover:text-foreground">{problem}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function SolutionSection({ solutionTitle, solutionPoints }: { solutionTitle: string; solutionPoints: string[] }) {
  return (
    <section className="py-10 md:py-12 lg:py-16 border-t border-border bg-muted/10">
      <div className="container-wide">
        <motion.div className="max-w-3xl" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={staggerContainer}>
          <motion.span variants={sectionVariant} className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">The Solution</motion.span>
          <motion.h2 variants={sectionVariant} className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-10">{solutionTitle}</motion.h2>
          <motion.div className="space-y-5" variants={staggerContainer}>
            {solutionPoints.map((point, index) => (
              <motion.div key={index} variants={itemVariant} className="flex items-start gap-4">
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <p className="text-foreground">{point}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function PlatformEcosystemsSection({ platformEcosystems }: { platformEcosystems: NonNullable<SubServicePageProps["platformEcosystems"]> }) {
  return (
    <section className="py-10 md:py-12 lg:py-16 border-t border-border">
      <div className="container-wide">
        <motion.div className="max-w-3xl" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={staggerContainer}>
          <motion.span variants={sectionVariant} className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">Extended Surface</motion.span>
          <motion.h3 variants={sectionVariant} className="font-display text-xl md:text-2xl font-semibold text-foreground mb-6">{platformEcosystems.title || "Platform-Native Performance Ecosystems"}</motion.h3>
          <motion.div className="space-y-4 mb-6" variants={staggerContainer}>
            {platformEcosystems.intro.map((paragraph, index) => (
              <motion.p key={index} variants={itemVariant} className="text-muted-foreground leading-relaxed">{paragraph}</motion.p>
            ))}
          </motion.div>
          <motion.p variants={itemVariant} className="text-sm text-muted-foreground/80 leading-relaxed mb-8">{platformEcosystems.platformMention}</motion.p>
          <motion.div className="relative mb-6" variants={problemStaggerContainer}>
            <motion.div className="absolute left-0 top-0 bottom-0 w-0.5 bg-primary/15" variants={problemAccentLineVariant} style={{ transformOrigin: "top" }} />
            <div className="space-y-3 pl-6">
              {platformEcosystems.problems.map((problem, index) => (
                <motion.p key={index} variants={problemBulletVariant} className="text-sm text-foreground/80">{problem}</motion.p>
              ))}
            </div>
          </motion.div>
          <motion.p variants={itemVariant} className="text-xs text-muted-foreground/60 leading-relaxed">{platformEcosystems.reassurance}</motion.p>
        </motion.div>
      </div>
    </section>
  );
}

function ServicesGridSection({ systemTitle, systemDescription, services }: Pick<SubServicePageProps, "systemTitle" | "systemDescription" | "services">) {
  return (
    <ServicesGrid
      label="What We Deliver"
      title={systemTitle}
      description={systemDescription}
      services={services.map((s) => ({ ...s, description: undefined }))}
    />
  );
}

function SystemShiftSection({ systemShift }: { systemShift: NonNullable<SubServicePageProps["systemShift"]> }) {
  return (
    <SystemShift
      title={systemShift.title}
      beforeTitle={systemShift.beforeTitle}
      afterTitle={systemShift.afterTitle}
      items={systemShift.items}
    />
  );
}

function ProcessSection({ processTitle, processSteps, timelinePhases }: Pick<SubServicePageProps, "processTitle" | "processSteps" | "timelinePhases">) {
  if (timelinePhases && timelinePhases.length > 0) {
    return <SystemsTimeline title={processTitle} phases={timelinePhases} label="Process" />;
  }
  return <EditorialSteps title={processTitle} steps={processSteps} label="Process" />;
}

function DataFlowchartSection({ dataFlowchart }: { dataFlowchart: NonNullable<SubServicePageProps["dataFlowchart"]> }) {
  return (
    <DataFlowchart
      title={dataFlowchart.title}
      label={dataFlowchart.label}
      description={dataFlowchart.description}
      nodes={dataFlowchart.nodes}
      connections={dataFlowchart.connections}
    />
  );
}

function CapabilityMatrixSection({ capabilityMatrix }: { capabilityMatrix: NonNullable<SubServicePageProps["capabilityMatrix"]> }) {
  return (
    <CapabilityMatrix
      title={capabilityMatrix.title}
      label={capabilityMatrix.label}
      description={capabilityMatrix.description}
      categories={capabilityMatrix.categories}
    />
  );
}

function EngagementShapeSection({ engagementShape }: { engagementShape: NonNullable<SubServicePageProps["engagementShape"]> }) {
  return (
    <EngagementShape
      title={engagementShape.title}
      description={engagementShape.description}
      phases={engagementShape.phases}
    />
  );
}

function TechStackSection({ technologyCategories, technologies, techTitle = "Tools & Platforms", techDescription }: Pick<SubServicePageProps, "technologyCategories" | "technologies" | "techTitle" | "techDescription">) {
  if (technologyCategories && technologyCategories.length > 0) {
    return (
      <TechnologyStack
        title={`${techTitle} We Use`}
        description={techDescription}
        categories={technologyCategories.map((cat) => ({ title: cat.title, icon: cat.icon, items: cat.items }))}
      />
    );
  }

  if (technologies && technologies.length > 0) {
    return (
      <section className="py-10 md:py-12 lg:py-16 border-t border-border">
        <div className="container-wide">
          <motion.div className="max-w-3xl mb-12" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={sectionVariant}>
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">{techTitle}</span>
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-4">Tools we work with.</h2>
            {techDescription && <p className="text-muted-foreground">{techDescription}</p>}
          </motion.div>
          <motion.div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8" initial="hidden" whileInView="visible" viewport={viewportOnceNear} variants={staggerContainer}>
            {technologies.map((tech) => (
              <motion.div key={tech.category} variants={itemVariant} className="border-l-2 border-border pl-4 py-2 hover:border-primary/50 transition-colors">
                <span className="text-xs text-muted-foreground uppercase tracking-wider block mb-3">{tech.category}</span>
                <ul className="space-y-2">{tech.items.map((item) => (<li key={item} className="text-sm text-foreground">{item}</li>))}</ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    );
  }

  return null;
}

function QualificationSection({ qualification }: Pick<SubServicePageProps, "qualification">) {
  return (
    <section className="py-10 md:py-12 lg:py-16 border-t border-border bg-muted/20">
      <div className="container-wide">
        <motion.div className="max-w-3xl mb-6 md:mb-10 lg:mb-12" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={sectionVariant}>
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">Qualification</span>
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground">Is this right for you?</h2>
        </motion.div>
        <motion.div className="grid md:grid-cols-2 gap-12" initial="hidden" whileInView="visible" viewport={viewportOnceNear} variants={staggerContainer}>
          <motion.div variants={itemVariant}>
            <h3 className="text-lg font-medium text-foreground mb-6">This is for you if...</h3>
            <ul className="space-y-4">
              {qualification.forYou.map((item) => (
                <li key={item} className="flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" /><span className="text-muted-foreground">{item}</span></li>
              ))}
            </ul>
          </motion.div>
          <motion.div variants={itemVariant}>
            <h3 className="text-lg font-medium text-foreground mb-6">This is not for you if...</h3>
            <ul className="space-y-4">
              {qualification.notForYou.map((item) => (
                <li key={item} className="flex items-start gap-3"><span className="text-muted-foreground/50 mt-0.5">×</span><span className="text-muted-foreground">{item}</span></li>
              ))}
            </ul>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function FAQsSection({ faqs }: Pick<SubServicePageProps, "faqs">) {
  return (
    <section className="py-10 md:py-12 lg:py-16 border-t border-border">
      <div className="container-wide">
        <motion.div className="max-w-3xl mb-6 md:mb-10 lg:mb-12" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={sectionVariant}>
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">FAQs</span>
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground">Common questions.</h2>
        </motion.div>
        <motion.div className="max-w-3xl space-y-8" initial="hidden" whileInView="visible" viewport={viewportOnceNear} variants={staggerContainer}>
          {faqs.map((faq, index) => (
            <motion.div key={index} variants={itemVariant} className="border-b border-border pb-8 last:border-0">
              <h3 className="text-lg font-medium text-foreground mb-3">{faq.question}</h3>
              <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function FinalCTASection({ finalCtaTitle, finalCtaDescription, finalCtaButtonText = "Start a serious conversation", ctaLink = "/contact" }: Pick<SubServicePageProps, "finalCtaTitle" | "finalCtaDescription" | "finalCtaButtonText" | "ctaLink">) {
  return (
    <section className="py-10 md:py-14 lg:py-20 border-t border-border bg-muted/20">
      <div className="container-wide">
        <motion.div className="max-w-2xl mx-auto text-center" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={sectionVariant}>
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-4">{finalCtaTitle}</h2>
          <p className="text-muted-foreground mb-8">{finalCtaDescription}</p>
          <Button size="lg" className="group" asChild><Link to={ctaLink}>{finalCtaButtonText}<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link></Button>
        </motion.div>
      </div>
    </section>
  );
}

// ─── Block Renderer ──────────────────────────────────────────────

function renderBlock(blockType: string, props: SubServicePageProps): React.ReactNode | null {
  switch (blockType) {
    case "hero":
      return <HeroSection categoryLabel={props.categoryLabel} headline={props.headline} subheadline={props.subheadline} heroSecondaryText={props.heroSecondaryText} ctaText={props.ctaText} ctaLink={props.ctaLink} />;
    case "problem":
      return <ProblemSection problemTitle={props.problemTitle} problems={props.problems} />;
    case "solution":
      if (!props.solutionTitle || !props.solutionPoints?.length) return null;
      return <SolutionSection solutionTitle={props.solutionTitle} solutionPoints={props.solutionPoints} />;
    case "platform_ecosystems":
      if (!props.platformEcosystems) return null;
      return <PlatformEcosystemsSection platformEcosystems={props.platformEcosystems} />;
    case "services_grid":
      return <ServicesGridSection systemTitle={props.systemTitle} systemDescription={props.systemDescription} services={props.services} />;
    case "system_shift":
      if (!props.systemShift?.items?.length) return null;
      return <SystemShiftSection systemShift={props.systemShift} />;
    case "process":
      return <ProcessSection processTitle={props.processTitle} processSteps={props.processSteps} timelinePhases={props.timelinePhases} />;
    case "data_flowchart":
      if (!props.dataFlowchart?.nodes?.length) return null;
      return <DataFlowchartSection dataFlowchart={props.dataFlowchart} />;
    case "capability_matrix":
      if (!props.capabilityMatrix?.categories?.length) return null;
      return <CapabilityMatrixSection capabilityMatrix={props.capabilityMatrix} />;
    case "engagement_shape":
      if (!props.engagementShape) return null;
      return <EngagementShapeSection engagementShape={props.engagementShape} />;
    case "tech_stack":
      return <TechStackSection technologyCategories={props.technologyCategories} technologies={props.technologies} techTitle={props.techTitle} techDescription={props.techDescription} />;
    case "qualification":
      return <QualificationSection qualification={props.qualification} />;
    case "faqs":
      if (!props.faqs?.length) return null;
      return <FAQsSection faqs={props.faqs} />;
    case "final_cta":
      return <FinalCTASection finalCtaTitle={props.finalCtaTitle} finalCtaDescription={props.finalCtaDescription} finalCtaButtonText={props.finalCtaButtonText} ctaLink={props.ctaLink} />;
    default:
      return null;
  }
}

// ─── Main Layout ─────────────────────────────────────────────────

const SubServicePageLayout = (props: SubServicePageProps) => {
  const {
    seoTitle, seoDescription, canonicalUrl, blocks,
  } = props;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: seoTitle,
    description: seoDescription,
    provider: { "@type": "Organization", name: "XperfoMax" },
  };

  // Determine section order: use CMS blocks if available, else default
  const blocksToRender = blocks && blocks.length > 0
    ? blocks
    : DEFAULT_BLOCK_ORDER;

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={seoTitle} description={seoDescription} canonicalUrl={canonicalUrl} schema={schema} />
      <Header />
      {blocksToRender
        .filter((b) => b.enabled)
        .map((block, i) => (
          <Fragment key={`${block.type}-${i}`}>
            {renderBlock(block.type, props)}
          </Fragment>
        ))}
      <Footer />
    </div>
  );
};

export default SubServicePageLayout;
